/**
 * Composants sociaux partagés par le menu (panneau latéral, page Amis) et la partie
 * (panneau Amis en jeu) : ligne d'ami avec sa présence, invitation reçue, demande d'ami,
 * formulaire d'ajout par le nom.
 */
import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Ban, Check, ChevronDown, ChevronRight, Ellipsis, LogIn, Mail, Send, UserMinus, UserPlus, Users, X } from 'lucide-preact';
import type { FriendInfo, FriendRequestInfo, InviteInfo } from '@snowchain/shared';
import { lobby } from './lobby';
import { menu } from './state';
import { act, invitableWorlds, inviteFriend, join, requestFriend } from './actions';
import { Avatar, Btn, Empty, presenceText, relTime } from './ui';

export type SocialMode = 'side' | 'page' | 'game';

/** Petit menu déroulant ancré sur un bouton (ferme au clic à côté et sur Échap). */
export function Popover(props: { button: (open: boolean, toggle: () => void) => ComponentChildren; children: (close: () => void) => ComponentChildren; align?: 'left' | 'right' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open]);
  return (
    <span class="mn-pop-anchor" ref={ref}>
      {props.button(open, () => setOpen(!open))}
      {open && <div class={`mn-pop mn-pop-${props.align ?? 'right'}`}>{props.children(() => setOpen(false))}</div>}
    </span>
  );
}

function InviteMenu(props: { f: FriendInfo }) {
  return (
    <Popover
      button={(open, toggle) => (
        <Btn size="sm" icon={UserPlus} onClick={toggle} title="Inviter sur une de mes parties">
          Inviter <ChevronDown size={12} style={{ transform: open ? 'rotate(180deg)' : undefined }} />
        </Btn>
      )}
    >
      {(close) => {
        const worlds = invitableWorlds();
        if (!worlds.length) return <p class="mn-pop-empty">Créez ou rejoignez une partie pour inviter vos amis.</p>;
        return (
          <>
            <p class="mn-pop-title">Inviter {props.f.name} sur...</p>
            {worlds.slice(0, 8).map((w) => (
              <button
                key={w.id}
                type="button"
                class="mn-pop-item"
                onClick={() => {
                  close();
                  void inviteFriend(props.f, w);
                }}
              >
                <span>{w.stationName || w.name}</span>
                <span class="mn-muted">{w.name}</span>
              </button>
            ))}
          </>
        );
      }}
    </Popover>
  );
}

function MoreMenu(props: { f: FriendInfo }) {
  const f = props.f;
  return (
    <Popover button={(_o, toggle) => <Btn size="sm" kind="ghost" icon={Ellipsis} title={`Plus d’actions pour ${f.name}`} onClick={toggle} />}>
      {(close) => (
        <>
          <button
            type="button"
            class="mn-pop-item"
            onClick={() => {
              close();
              void act({ op: 'friend.remove', id: f.id }, 'Ami retiré', `${f.name} ne fait plus partie de vos amis.`);
            }}
          >
            <span>
              <UserMinus size={13} /> Retirer des amis
            </span>
          </button>
          <button
            type="button"
            class="mn-pop-item mn-pop-danger"
            onClick={() => {
              close();
              void act({ op: 'block', id: f.id }, 'Joueur bloqué', `${f.name} ne peut plus vous écrire ni vous inviter.`);
            }}
          >
            <span>
              <Ban size={13} /> Bloquer
            </span>
          </button>
        </>
      )}
    </Popover>
  );
}

/**
 * Ligne d'ami. En jeu (`game`), `world` est la partie en cours : bouton « Inviter » ;
 * rejoindre un ami ailleurs recharge la page sur sa partie.
 */
export function FriendRow(props: { f: FriendInfo; mode: SocialMode; world?: { id: string; name: string } | null; invited?: boolean; onInvite?: () => void; onJoin?: (worldId: string) => void }) {
  const f = props.f;
  const p = f.presence;
  const target = p.state === 'game' && p.world ? p.world : null;
  const sameWorld = !!target && target.id === props.world?.id;
  const doJoin = () => {
    if (!target) return;
    if (props.onJoin) props.onJoin(target.id);
    else join(target.id);
  };
  return (
    <div class={`mn-friend mn-friend-${p.state}`}>
      <Avatar id={f.id} name={f.name} size={props.mode === 'page' ? 36 : 30} presence={p.state} />
      <div class="mn-friend-text">
        <span class="mn-friend-name">{f.name}</span>
        <span class="mn-friend-presence">{sameWorld ? 'Dans cette partie' : presenceText(p)}</span>
      </div>
      <span class="mn-friend-actions">
        {props.mode === 'game' && !sameWorld && (
          <Btn size="sm" icon={props.invited ? Check : Send} disabled={props.invited} title={props.invited ? 'Invitation envoyée' : `Inviter ${f.name} dans cette partie`} onClick={props.onInvite}>
            {props.invited ? 'Invité' : 'Inviter'}
          </Btn>
        )}
        {target && !sameWorld && props.mode !== 'game' && (
          <Btn size="sm" kind={props.mode === 'side' ? 'ghost' : 'secondary'} icon={LogIn} title={`Rejoindre ${f.name} sur « ${target.name} »`} onClick={doJoin}>
            {props.mode === 'page' ? 'Rejoindre' : undefined}
          </Btn>
        )}
        {props.mode === 'page' && <InviteMenu f={f} />}
        {props.mode === 'page' && <MoreMenu f={f} />}
      </span>
    </div>
  );
}

export function InviteCard(props: { inv: InviteInfo; onJoin?: (worldId: string) => void }) {
  const i = props.inv;
  return (
    <div class="mn-invite">
      <div class="mn-invite-head">
        <Mail size={14} />
        <span>
          <b>{i.from.name}</b> vous invite
        </span>
        <span class="mn-muted mn-small">{relTime(i.at)}</span>
      </div>
      <div class="mn-invite-world">
        <span class="mn-invite-station">{i.world.stationName || i.world.name}</span>
        <span class="mn-muted">{i.world.name}</span>
      </div>
      <div class="mn-invite-actions">
        <Btn size="sm" kind="primary" icon={LogIn} onClick={() => (props.onJoin ? props.onJoin(i.world.id) : join(i.world.id))}>
          Rejoindre
        </Btn>
        <Btn size="sm" kind="ghost" icon={X} title="Ignorer l'invitation" onClick={() => void act({ op: 'invite.dismiss', id: i.id })}>
          Ignorer
        </Btn>
      </div>
    </div>
  );
}

export function RequestRow(props: { r: FriendRequestInfo; dir: 'in' | 'out'; compact?: boolean }) {
  const r = props.r;
  return (
    <div class="mn-friend">
      <Avatar id={r.id} name={r.name} size={props.compact ? 30 : 36} />
      <div class="mn-friend-text">
        <span class="mn-friend-name">{r.name}</span>
        <span class="mn-friend-presence">{props.dir === 'in' ? (props.compact ? `Demande ${relTime(r.at)}` : `Veut devenir votre ami · ${relTime(r.at)}`) : `Demande envoyée ${relTime(r.at)}`}</span>
      </div>
      <span class="mn-friend-actions">
        {props.dir === 'in' ? (
          <>
            <Btn size="sm" kind="primary" icon={Check} title={`Accepter ${r.name}`} onClick={() => void act({ op: 'friend.accept', id: r.id }, 'Nouvel ami', `${r.name} fait maintenant partie de vos amis.`)}>
              {props.compact ? undefined : 'Accepter'}
            </Btn>
            <Btn size="sm" kind="ghost" icon={X} title={`Refuser ${r.name}`} onClick={() => void act({ op: 'friend.decline', id: r.id })}>
              {props.compact ? undefined : 'Refuser'}
            </Btn>
            {!props.compact && <Btn size="sm" kind="ghost" icon={Ban} title={`Bloquer ${r.name}`} onClick={() => void act({ op: 'block', id: r.id }, 'Joueur bloqué', `${r.name} ne peut plus vous écrire ni vous inviter.`)} />}
          </>
        ) : (
          <Btn size="sm" kind="ghost" icon={X} onClick={() => void act({ op: 'friend.cancel', id: r.id })}>
            Annuler
          </Btn>
        )}
      </span>
    </div>
  );
}

export function AddFriendForm(props: { compact?: boolean }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <form
      class={`mn-add${props.compact ? ' mn-add-compact' : ''}`}
      onSubmit={async (e) => {
        e.preventDefault();
        const n = name.trim();
        if (!n || busy) return;
        setBusy(true);
        if (await requestFriend(n)) setName('');
        setBusy(false);
      }}
    >
      <input
        value={name}
        maxLength={24}
        placeholder="Nom exact du joueur"
        aria-label="Nom du joueur à ajouter"
        autoComplete="off"
        spellcheck={false}
        onInput={(e) => setName((e.target as HTMLInputElement).value)}
      />
      <Btn type="submit" kind="primary" size={props.compact ? 'sm' : 'md'} icon={UserPlus} disabled={busy || name.trim().length < 3}>
        {props.compact ? undefined : 'Ajouter'}
      </Btn>
    </form>
  );
}

/** Panneau « Amis » à droite du menu : invitations, demandes, amis en ligne puis hors ligne. */
export function SocialSidebar() {
  const s = lobby.social.value;
  const [showOffline, setShowOffline] = useState(false);
  const online = s.friends.filter((f) => f.presence.state !== 'offline');
  const offline = s.friends.filter((f) => f.presence.state === 'offline');
  return (
    <aside class="mn-side" aria-label="Amis">
      <div class="mn-side-head">
        <Users size={15} />
        <span class="mn-side-title">Amis</span>
        <span class="mn-muted mn-small">
          {online.length} en ligne · {s.friends.length}
        </span>
        <button type="button" class="mn-ibtn" title="Gérer mes amis" onClick={() => (menu.view.value = 'friends')}>
          <ChevronRight size={15} />
        </button>
      </div>
      <AddFriendForm compact />
      <div class="mn-side-scroll">
        {s.invites.length > 0 && (
          <div class="mn-side-group">
            <p class="mn-side-label">Invitations · {s.invites.length}</p>
            {s.invites.map((i) => (
              <InviteCard key={i.id} inv={i} />
            ))}
          </div>
        )}
        {s.incoming.length > 0 && (
          <div class="mn-side-group">
            <p class="mn-side-label">Demandes reçues · {s.incoming.length}</p>
            {s.incoming.slice(0, 4).map((r) => (
              <RequestRow key={r.id} r={r} dir="in" compact />
            ))}
            {s.incoming.length > 4 && (
              <button
                type="button"
                class="mn-link"
                onClick={() => {
                  menu.friendsTab.value = 'requests';
                  menu.view.value = 'friends';
                }}
              >
                Toutes les demandes <ChevronRight size={13} />
              </button>
            )}
          </div>
        )}
        <div class="mn-side-group">
          {s.friends.length === 0 ? (
            <Empty icon={Users} title="Pas encore d’amis">
              Ajoutez un joueur par son nom exact pour voir quand il joue et l’inviter dans vos parties.
            </Empty>
          ) : (
            <>
              {online.length > 0 && <p class="mn-side-label">En ligne · {online.length}</p>}
              {online.map((f) => (
                <FriendRow key={f.id} f={f} mode="side" />
              ))}
              {offline.length > 0 && (
                <button type="button" class="mn-side-label mn-side-toggle" onClick={() => setShowOffline(!showOffline)} aria-expanded={showOffline}>
                  Hors ligne · {offline.length} <ChevronDown size={12} style={{ transform: showOffline ? 'rotate(180deg)' : undefined }} />
                </button>
              )}
              {showOffline && offline.map((f) => <FriendRow key={f.id} f={f} mode="side" />)}
            </>
          )}
        </div>
      </div>
    </aside>
  );
}
